import styled from "@emotion/native"
import { ButtonV2, Spacing, Text } from "../../ui-components"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper"
import { Image, Linking, Platform, ScrollView } from "react-native"
import MessageBubbleIcon from "../../assets/onboarding-v2/message-bubble-icon.png"

type HeadsUpScreenUIProps = {
  onProceed: () => void
}

const REQUIREMENTS = ["Be a U.S. Citizen", "Have your I.D. handy", "Bank account details"]

export function HeadsUpUI({ onProceed }: HeadsUpScreenUIProps) {
  function forceBrowserOpen(url: string) {
    if (Platform.OS === "android") {
      Linking.openURL(`googlechrome://navigate?url=${url}`)
      return
    }

    Linking.openURL(url)
  }

  return (
    <OnboardingScreenWrapper stepNumber={0}>
      <Container>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 20,
            paddingTop: 52,
            paddingBottom: 20,
          }}
          alwaysBounceVertical={false}
        >
          <Text fontSize={32} lineHeight={38.4} fontFamily="FFFAcidGrotesk-Medium" color="gray900">
            Set up SynchPay in{"\n"}under 5 minutes
          </Text>
          <Spacing top={52} />
          <Cards>
            <InfoCard>
              <MessageBubbleImage source={MessageBubbleIcon} resizeMode="contain" />
              <Text
                fontSize={16}
                lineHeight={24}
                fontFamily="FFFAcidGrotesk-Book"
                color="grayscale700"
              >
                We're going to send you a few two-factor authentication texts in order to keep your
                money safe and secure.
              </Text>
            </InfoCard>
            <InfoCard>
              <Text
                fontSize={16}
                lineHeight={24}
                fontFamily="FFFAcidGrotesk-Book"
                color="grayscale700"
              >
                What you'll need to set up SynchPay:
              </Text>
              <RequirementsList>
                {REQUIREMENTS.map((requirement) => (
                  <RequirementRow key={requirement}>
                    <Bullet />
                    <Text
                      fontSize={16}
                      lineHeight={24}
                      fontFamily="FFFAcidGrotesk-Book"
                      color="grayscale700"
                      style={{ flex: 1 }}
                    >
                      {requirement}
                    </Text>
                  </RequirementRow>
                ))}
              </RequirementsList>
            </InfoCard>
            <InfoCard>
              <Text
                fontSize={16}
                lineHeight={24}
                fontFamily="FFFAcidGrotesk-Book"
                color="grayscale700"
              >
                By tapping 'Agree' you agree with our{" "}
                <Text
                  color="royalBlue"
                  fontFamily="FFFAcidGrotesk-Book"
                  fontSize={16}
                  lineHeight={24}
                  onPress={() => forceBrowserOpen("https://synchpay.com/terms-and-conditions/")}
                >
                  Terms & Conditions
                </Text>{" "}
                and our{" "}
                <Text
                  color="royalBlue"
                  fontFamily="FFFAcidGrotesk-Book"
                  fontSize={16}
                  lineHeight={24}
                  onPress={() => forceBrowserOpen("https://synchpay.com/privacy-policy/")}
                >
                  Privacy Policy
                </Text>
                .
              </Text>
            </InfoCard>
          </Cards>
        </ScrollView>
        <ButtonContainer>
          <Spacing top={25} left={20} right={20}>
            <ButtonV2
              title="Agree"
              variant="blue2"
              onPress={onProceed}
              customHeight={60}
              testID="headsUpAgreeButton"
            />
          </Spacing>
        </ButtonContainer>
        {Platform.OS !== "ios" && <Spacing top={20} />}
      </Container>
    </OnboardingScreenWrapper>
  )
}

const Container = styled.View`
  flex: 1;
  max-width: 500px;
  width: 100%;
  align-self: center;
  justify-content: space-between;
`

const Cards = styled.View`
  gap: 16px;
`

const InfoCard = styled.View`
  background-color: ${({ theme }) => theme.text.grayscale100};
  border-radius: 20px;
  padding: 20px;
  gap: 12px;
`

const MessageBubbleImage = styled(Image)`
  width: 52px;
  height: 55px;
`

const RequirementsList = styled.View`
  gap: 0px;
`

const RequirementRow = styled.View`
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding-left: 8px;
`

const Bullet = styled.View`
  width: 4px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.text.grayscale700};
  margin-top: 10px;
`

const ButtonContainer = styled.View`
  border-top-width: 2px;
  border-color: ${({ theme }) => theme.v2.screens.terms.buttonContainerBorderColor};
`
